import update from 'immutability-helper';
import { deduplicateArray } from '../languageExtensions/array.js';
import { objectKeys } from '../languageExtensions/object.js';
import 'lodash-unified';

/**
 * Wraps string values in double quotes and escapes inner double quotes in order
 * to prevent invalid algolia filter data. Throws an error if an empty string
 * is passed, as that is not a valid filter.
 *
 * Examples:
 * ```
 * // Basic string
 * escapeAlgoliaStringFilter('test') ➡️ '"test"'
 *
 * // Prevent double quotes from being treated as the end of the filtered string
 * escapeAlgoliaStringFilter('abc"def') ➡️ '"abc\"def"'
 *
 * // Prevent space from triggering "attribute with no value" error
 * escapeAlgoliaStringFilter(' ') ➡️ '" "'
 *
 * // Prevent hyphen in push ID from being treated as a negation operator. This
 * // format does not work with facetFilters. Use filters if filtering by IDs
 * // starting with a hyphen.
 * escapeAlgoliaStringFilter('-NbvInR1kw9PYGC-iNTi') ➡️ '"-NbvInR1kw9PYGC-iNTi"'
 * ```
 */

const escapeAlgoliaStringFilter = string => {
  if (!string) throw Error('Empty string is not a valid algolia filter');
  const escaped = string.replace(/"/g, '\\"');
  return `"${escaped}"`;
};
/**
 * Remove extra properties algolia adds to search results, as well as the
 * `algolia` property, which we use to store reshaped data in algolia for
 * indexing purposes.
 */

const stripAlgoliaProperties = hit => {
  const hitExtraKeysObj = {
    _distinctSeqID: true,
    _highlightResult: true,
    _rankingInfo: true,
    _snippetResult: true,
    objectID: true
  };
  const geoExtraKeysObj = {
    _geoloc: true,
    _tags: true,
    _sortingDate: true,
    _writeTimestamp: true,
    objectID: true
  };
  const customExtraKeys = ['algolia' // Used on User in algolia to store reshaped data for indexing
  ];
  const extraKeys = deduplicateArray([...objectKeys(hitExtraKeysObj), ...objectKeys(geoExtraKeysObj), ...customExtraKeys]);
  return update(hit, {
    $unset: extraKeys
  });
};
/** This is the default value used for pagination */

const PAGINATION_HITS_PER_PAGE = 10;

export { PAGINATION_HITS_PER_PAGE, escapeAlgoliaStringFilter, stripAlgoliaProperties };
